import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Input, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../../app/store";
import {
  useLiftWatchFleetQuery,
  type LiftWatchFleetResponse,
  type LiftWatchServer,
  type LiftWatchSeverity,
} from "../../../redux/liftwatch/liftWatchAPI";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";

export type AttentionSeverity = "all" | "critical" | "warning" | "info";
export type FleetSort = "priority" | "name" | "account";
const priority: Record<LiftWatchSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
  healthy: 3,
};

export function needsAttention(server: LiftWatchServer): boolean {
  return server.connection !== "awaiting_first_report" && server.overall_status !== "healthy";
}

export function filterFleet(
  servers: LiftWatchServer[],
  search: string,
  severity: AttentionSeverity,
  sort: FleetSort,
): LiftWatchServer[] {
  const query = search.trim().toLocaleLowerCase();
  return servers
    .filter(
      (server) =>
        (severity === "all" || server.overall_status === severity) &&
        (!query ||
          [server.name, server.org_name, server.org_id, server.location ?? ""].some((value) =>
            value.toLocaleLowerCase().includes(query),
          )),
    )
    .sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "account")
        return a.org_name.localeCompare(b.org_name) || a.name.localeCompare(b.name);
      return (
        priority[a.overall_status] - priority[b.overall_status] || a.name.localeCompare(b.name)
      );
    });
}

function ServerDetails({ server }: { server: LiftWatchServer }) {
  const [t] = useTranslation("global");
  const unknown = t("liftWatch.unknown", { defaultValue: "Unknown" });
  const connectionLabels = {
    online: t("liftWatch.online", { defaultValue: "Online" }),
    offline: t("liftWatch.offline", { defaultValue: "Offline" }),
    awaiting_first_report: t("liftWatch.awaiting", { defaultValue: "Awaiting first report" }),
  };
  return (
    <div className="liftWatch__details">
      <dl>
        <div>
          <dt>{t("liftWatch.connection", { defaultValue: "Connection" })}</dt>
          <dd>{connectionLabels[server.connection]}</dd>
        </div>
        <div>
          <dt>{t("liftWatch.software", { defaultValue: "Software" })}</dt>
          <dd>
            {t("liftWatch.softwareDetail", {
              defaultValue: "Agent {{agent}} · latest {{latest}} · OS {{os}}",
              agent: server.software.agent ?? unknown,
              latest: server.software.latest ?? unknown,
              os: server.software.os ?? unknown,
            })}
          </dd>
        </div>
        <div>
          <dt>{t("liftWatch.services", { defaultValue: "Services · 30-day uptime" })}</dt>
          <dd>
            {t("liftWatch.servicesDetail", {
              defaultValue: "{{running}} / {{total}} running · {{uptime}}",
              running: server.services.running ?? unknown,
              total: server.services.total ?? unknown,
              uptime:
                server.services.uptime_30d_pct == null
                  ? unknown
                  : `${server.services.uptime_30d_pct.toFixed(2)}%`,
            })}
            {server.connection === "offline" && (
              <small>{t("liftWatch.lastKnown", { defaultValue: "Last known state" })}</small>
            )}
          </dd>
        </div>
        <div>
          <dt>{t("liftWatch.security", { defaultValue: "Security" })}</dt>
          <dd>{server.security_summary ?? unknown}</dd>
        </div>
      </dl>
      {server.issue.detail && <p>{server.issue.detail}</p>}
      {server.error_samples.length > 0 && (
        <div className="liftWatch__errors">
          <h3>{t("liftWatch.errorSamples", { defaultValue: "Recent classified errors" })}</h3>
          <ul>
            {server.error_samples.map((sample, index) => (
              <li key={`${sample.ts}-${index}`}>
                <time dateTime={sample.ts}>{new Date(sample.ts).toLocaleString()}</time>{" "}
                <strong>{sample.service}</strong> {sample.line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FleetTable({
  servers,
  expanded,
  onExpand,
  inventory = false,
}: {
  servers: LiftWatchServer[];
  expanded: string | null;
  onExpand: (id: string | null) => void;
  inventory?: boolean;
}) {
  const [t] = useTranslation("global");
  const labels: Record<LiftWatchSeverity, string> = {
    healthy: t("liftWatch.healthy", { defaultValue: "Healthy" }),
    warning: t("liftWatch.warning", { defaultValue: "Warning" }),
    critical: t("liftWatch.critical", { defaultValue: "Critical" }),
    info: t("liftWatch.info", { defaultValue: "Information" }),
  };
  const columns: ColumnsType<LiftWatchServer> = [
    {
      title: t("liftWatch.server", { defaultValue: "Edge server" }),
      key: "name",
      render: (_, server) => (
        <>
          <strong>{server.name}</strong>
          <small>
            {server.location ??
              t("liftWatch.unknownLocation", { defaultValue: "Location unknown" })}
          </small>
        </>
      ),
    },
    {
      title: t("liftWatch.account", { defaultValue: "Account" }),
      key: "org",
      render: (_, server) => (
        <>
          {server.org_name}
          <small>{server.org_id}</small>
        </>
      ),
    },
    {
      title: t("liftWatch.status", { defaultValue: "Status" }),
      key: "issue",
      render: (_, server) => (
        <>
          <Tag className={`liftWatch__status liftWatch__status--${server.overall_status}`}>
            {server.connection === "awaiting_first_report"
              ? t("liftWatch.awaiting", { defaultValue: "Awaiting first report" })
              : labels[server.overall_status]}
          </Tag>
          <span>{server.connection !== "awaiting_first_report" && server.issue.title}</span>
        </>
      ),
    },
    {
      title: t("liftWatch.lastSeen", { defaultValue: "Last seen" }),
      key: "last_seen",
      render: (_, server) =>
        server.last_seen ? (
          <time dateTime={server.last_seen}>{new Date(server.last_seen).toLocaleString()}</time>
        ) : (
          t("liftWatch.notAvailable", { defaultValue: "N/A" })
        ),
    },
  ];
  return (
    <Table<LiftWatchServer>
      className="liftWatch__table"
      rowKey="server_id"
      columns={columns}
      dataSource={servers}
      pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
      scroll={{ x: 760 }}
      locale={{
        emptyText: inventory
          ? t("liftWatch.noInventory", { defaultValue: "No inventory servers match your search." })
          : t("liftWatch.noExceptions", { defaultValue: "No exceptions match your search." }),
      }}
      expandable={{
        expandedRowKeys: expanded ? [expanded] : [],
        onExpand: (open, server) => onExpand(open ? server.server_id : null),
        expandedRowRender: (server) => <ServerDetails server={server} />,
        expandRowByClick: true,
        expandIcon: ({ expanded: open, onExpand: toggle, record }) => (
          <button
            type="button"
            className="liftWatch__expand"
            aria-expanded={open}
            aria-label={t("liftWatch.toggleDetails", {
              defaultValue: "Details for {{name}}",
              name: record.name,
            })}
            onClick={(event) => {
              event.stopPropagation();
              toggle(record, event);
            }}
          >
            {open ? "−" : "+"}
          </button>
        ),
      }}
    />
  );
}

export function LiftWatchFleet({ data }: { data: LiftWatchFleetResponse }) {
  const [t] = useTranslation("global");
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState<AttentionSeverity>("all");
  const [sort, setSort] = useState<FleetSort>("priority");
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const attention = useMemo(() => data.servers.filter(needsAttention), [data.servers]);
  const rows = useMemo(
    () => filterFleet(attention, search, severity, sort),
    [attention, search, severity, sort],
  );
  const inventory = useMemo(
    () =>
      filterFleet(
        data.servers.filter((server) => !needsAttention(server)),
        inventorySearch,
        "all",
        "name",
      ),
    [data.servers, inventorySearch],
  );
  const { health, metrics } = data;
  const reported = Math.max(0, health.total - health.awaiting_first_report);
  const healthyPct =
    reported > 0
      ? `${((100 * health.healthy) / reported).toFixed(1)}%`
      : t("liftWatch.notAvailable", { defaultValue: "N/A" });
  const unknown = t("liftWatch.unknown", { defaultValue: "Unknown" });
  const cards = [
    {
      key: "connected",
      label: t("liftWatch.connected", { defaultValue: "Connected to cloud" }),
      value: `${metrics.connected.count} / ${metrics.connected.total}`,
      note: t("liftWatch.offlineCount", {
        defaultValue: "{{count}} servers offline",
        count: metrics.connected.offline,
      }),
    },
    {
      key: "versions",
      label: t("liftWatch.versions", { defaultValue: "Software up to date" }),
      value: `${metrics.versions.up_to_date} / ${metrics.versions.total}`,
      note: t("liftWatch.versionsNote", {
        defaultValue: "{{behind}} behind · {{unknown}} unknown · latest {{latest}}",
        behind: metrics.versions.behind,
        unknown: metrics.versions.unknown,
        latest: metrics.versions.latest ?? unknown,
      }),
    },
    {
      key: "security",
      label: t("liftWatch.incidents", { defaultValue: "Security incidents" }),
      value: metrics.security.open_incidents,
      note: t("liftWatch.incidentsNote", {
        defaultValue: "{{count}} high severity",
        count: metrics.security.high_severity,
      }),
    },
    {
      key: "uptime",
      label: t("liftWatch.uptime", { defaultValue: "Service uptime" }),
      value:
        metrics.uptime_30d.fleet_pct == null
          ? unknown
          : `${metrics.uptime_30d.fleet_pct.toFixed(2)}%`,
      note: t("liftWatch.uptimeNote", {
        defaultValue: "Last 30 days · target {{target}}%",
        target: metrics.uptime_30d.target_pct,
      }),
    },
    {
      key: "errors",
      label: t("liftWatch.errors", { defaultValue: "System errors · 24h" }),
      value: metrics.errors_24h.count ?? unknown,
      note: t("liftWatch.errorsNote", {
        defaultValue: "{{servers}} servers · top server {{server}}",
        servers: metrics.errors_24h.servers,
        server:
          metrics.errors_24h.top_server ?? t("liftWatch.notAvailable", { defaultValue: "N/A" }),
      }),
    },
  ];
  let healthHeading: string;
  if (reported === 0) {
    healthHeading = t("liftWatch.awaitingFleet", { defaultValue: "Waiting for fleet reports" });
  } else if (attention.length > 0) {
    healthHeading = t("liftWatch.attentionHeading", { defaultValue: "Your fleet needs attention" });
  } else {
    healthHeading = t("liftWatch.healthyHeading", {
      defaultValue: "Your reported fleet is healthy",
    });
  }
  return (
    <>
      <header className="liftWatch__heading">
        <div>
          <h1>{t("liftWatch.title", { defaultValue: "Fleet health" })}</h1>
          <p>
            {t("liftWatch.subtitle", {
              defaultValue: "{{servers}} edge servers across {{accounts}} accounts",
              servers: health.total,
              accounts: health.accounts,
            })}
          </p>
        </div>
        <p>
          {t("liftWatch.snapshot", { defaultValue: "Snapshot" })}{" "}
          <time dateTime={data.snapshot_at}>{new Date(data.snapshot_at).toLocaleString()}</time>
        </p>
      </header>
      <section
        className="liftWatch__health"
        aria-label={t("liftWatch.overallHealth", { defaultValue: "Overall fleet health" })}
      >
        <div className="liftWatch__healthTop">
          <div>
            <h2>{healthHeading}</h2>
            <p>
              {t("liftWatch.attentionCount", {
                defaultValue: "{{count}} servers need attention",
                count: attention.length,
              })}
            </p>
          </div>
          <div className="liftWatch__healthNumber">
            {healthyPct}
            <small>
              {t("liftWatch.healthyPercentage", { defaultValue: "of reported servers healthy" })}
            </small>
          </div>
        </div>
        <div
          className="liftWatch__healthBar"
          role="img"
          aria-label={t("liftWatch.healthDescription", {
            defaultValue:
              "{{healthy}} healthy, {{warning}} warning, {{critical}} critical; {{awaiting}} awaiting first report",
            healthy: health.healthy,
            warning: health.warning,
            critical: health.critical,
            awaiting: health.awaiting_first_report,
          })}
        >
          {(["healthy", "warning", "critical"] as const).map((status) => (
            <span
              key={status}
              className={`liftWatch__segment--${status}`}
              style={{ width: `${health.total ? (health[status] / health.total) * 100 : 0}%` }}
            />
          ))}
        </div>
        <div className="liftWatch__legend">
          <span>
            {t("liftWatch.healthyCount", {
              defaultValue: "{{count}} healthy",
              count: health.healthy,
            })}
          </span>
          <span>
            {t("liftWatch.warningCount", {
              defaultValue: "{{count}} warning",
              count: health.warning,
            })}
          </span>
          <span>
            {t("liftWatch.criticalCount", {
              defaultValue: "{{count}} critical",
              count: health.critical,
            })}
          </span>
          <span>
            {t("liftWatch.awaitingCount", {
              defaultValue: "{{count}} awaiting first report",
              count: health.awaiting_first_report,
            })}
          </span>
        </div>
      </section>
      <section
        className="liftWatch__metrics"
        aria-label={t("liftWatch.statistics", { defaultValue: "Fleet statistics" })}
      >
        {cards.map((card) => (
          <Card key={card.key} className="liftWatch__metric">
            <h2>{card.label}</h2>
            <strong>{card.value}</strong>
            <p>{card.note}</p>
          </Card>
        ))}
      </section>
      <section aria-labelledby="liftWatch-attention-title">
        <div className="liftWatch__sectionHeading">
          <h2 id="liftWatch-attention-title">
            {t("liftWatch.needsAttention", { defaultValue: "Needs attention" })}{" "}
            <span>{attention.length}</span>
          </h2>
          <p>
            {t("liftWatch.attentionHelp", {
              defaultValue: "Healthy servers stay out of the way. Select a server to investigate.",
            })}
          </p>
        </div>
        <div className="liftWatch__toolbar">
          <label htmlFor="liftWatch-search">
            {t("liftWatch.search", { defaultValue: "Account or edge server" })}
            <Input
              id="liftWatch-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label htmlFor="liftWatch-severity">
            {t("liftWatch.severity", { defaultValue: "Severity" })}
            <select
              id="liftWatch-severity"
              value={severity}
              onChange={(event) => setSeverity(event.target.value as AttentionSeverity)}
            >
              <option value="all">
                {t("liftWatch.allSeverities", { defaultValue: "All severities" })}
              </option>
              <option value="critical">
                {t("liftWatch.critical", { defaultValue: "Critical" })}
              </option>
              <option value="warning">{t("liftWatch.warning", { defaultValue: "Warning" })}</option>
              <option value="info">{t("liftWatch.info", { defaultValue: "Information" })}</option>
            </select>
          </label>
          <label htmlFor="liftWatch-sort">
            {t("liftWatch.sort", { defaultValue: "Sort by" })}
            <select
              id="liftWatch-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value as FleetSort)}
            >
              <option value="priority">
                {t("liftWatch.priority", { defaultValue: "Highest priority first" })}
              </option>
              <option value="name">{t("liftWatch.server", { defaultValue: "Edge server" })}</option>
              <option value="account">{t("liftWatch.account", { defaultValue: "Account" })}</option>
            </select>
          </label>
        </div>
        <FleetTable servers={rows} expanded={expanded} onExpand={setExpanded} />
        <p className="liftWatch__resultCount" aria-live="polite">
          {t("liftWatch.resultCount", {
            defaultValue: "Showing {{shown}} of {{total}} servers needing attention",
            shown: rows.length,
            total: attention.length,
          })}
        </p>
      </section>
      <section
        className="liftWatch__inventory"
        aria-label={t("liftWatch.inventory", {
          defaultValue: "Healthy fleet and rollout inventory",
        })}
      >
        <Button
          aria-expanded={inventoryOpen}
          aria-controls="liftWatch-inventory"
          onClick={() => setInventoryOpen(!inventoryOpen)}
        >
          {t("liftWatch.browseInventory", {
            defaultValue: "Browse healthy fleet and awaiting servers",
          })}
        </Button>
        {inventoryOpen && (
          <div id="liftWatch-inventory">
            <p>
              {t("liftWatch.inventoryHelp", {
                defaultValue:
                  "Awaiting-first-report servers are inventory only, not health incidents.",
              })}
            </p>
            <label htmlFor="liftWatch-inventory-search">
              {t("liftWatch.inventorySearch", { defaultValue: "Search inventory" })}
              <Input
                id="liftWatch-inventory-search"
                type="search"
                value={inventorySearch}
                onChange={(event) => setInventorySearch(event.target.value)}
              />
            </label>
            <FleetTable servers={inventory} expanded={expanded} onExpand={setExpanded} inventory />
          </div>
        )}
      </section>
    </>
  );
}

export default function LiftWatchPage() {
  const [t] = useTranslation("global");
  const { userLogged, theme } = useSelector((state: RootState) => state.home);
  const authorized = process.env.REACT_APP_ENVIRONMENT !== "OFFLINE" && isMasterAdmin(userLogged);
  const { data, error, isLoading, isFetching, refetch } = useLiftWatchFleetQuery(authorized);
  const forbidden = error && "status" in error && (error.status === 401 || error.status === 403);
  return (
    <div className={`liftWatch__page liftWatch__page--${theme}`}>
      {!authorized || forbidden ? (
        <Alert
          type="warning"
          showIcon
          message={t("liftWatch.unauthorized", {
            defaultValue: "LiftWatch is available to master administrators in online mode only.",
          })}
        />
      ) : (
        <>
          {isLoading && (
            <p role="status">{t("liftWatch.loading", { defaultValue: "Loading fleet health…" })}</p>
          )}
          {error && (
            <Alert
              type="error"
              showIcon
              message={t("liftWatch.loadError", {
                defaultValue:
                  "Fleet health could not be refreshed. Any displayed snapshot may be out of date.",
              })}
              action={
                <Button onClick={() => refetch()}>
                  {t("liftWatch.retry", { defaultValue: "Retry" })}
                </Button>
              }
            />
          )}
          {data && <LiftWatchFleet data={data} />}
          {isFetching && !isLoading && (
            <p role="status">
              {t("liftWatch.refreshing", { defaultValue: "Refreshing snapshot…" })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
